import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import axiosInstance from '../../utils/axios';
import BibleLogo from '../common/BibleLogo';
import AnimatedBubblesBackground from './AnimatedBubblesBackground';

const ACCENT = '#1976D2'; // Professional blue

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  width: '100vw',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #e3f0ff 0%, #f8fafc 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Blob = styled('div')(() => ({
  position: 'absolute',
  borderRadius: '50%',
  filter: 'blur(60px)',
  opacity: 0.5,
  zIndex: 0,
  pointerEvents: 'none',
}));

const float1 = keyframes`
  0% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-30px) scale(1.08); }
  100% { transform: translateY(0) scale(1); }
`;
const float2 = keyframes`
  0% { transform: translateY(0) scale(1); }
  50% { transform: translateY(25px) scale(0.95); }
  100% { transform: translateY(0) scale(1); }
`;
const float3 = keyframes`
  0% { transform: translateX(0) scale(1); }
  50% { transform: translateX(30px) scale(1.05); }
  100% { transform: translateX(0) scale(1); }
`;
const float4 = keyframes`
  0% { transform: translateX(0) scale(1); }
  50% { transform: translateX(-20px) scale(1.1); }
  100% { transform: translateX(0) scale(1); }
`;

const Bubble = styled('div')(() => ({
  position: 'absolute',
  borderRadius: '50%',
  zIndex: 0,
  pointerEvents: 'none',
  boxShadow: '0 8px 32px 8px rgba(80,120,200,0.10), 0 2px 8px 0px rgba(255,255,255,0.25) inset',
  background: 'radial-gradient(circle at 60% 30%, rgba(255,255,255,0.7) 0%, rgba(180,200,255,0.3) 60%, rgba(120,180,255,0.15) 100%)',
  transition: 'all 0.5s',
}));

const BubbleBackground = styled('div')(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  zIndex: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
}));

const GradientPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  padding: theme.spacing(4),
  background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(240,248,255,0.98) 100%)',
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(80,120,200,0.10)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(200,220,255,0.25)',
  maxWidth: '600px',
  width: '100%',
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${ACCENT} 30%, #90caf9 90%)`,
  color: 'white',
  padding: '12px 30px',
  borderRadius: '25px',
  fontSize: '1rem',
  fontWeight: 'bold',
  textTransform: 'none',
  boxShadow: `0 3px 5px 2px ${ACCENT}22`,
  '&:hover': {
    background: `linear-gradient(45deg, #90caf9 30%, ${ACCENT} 90%)`,
    boxShadow: `0 5px 8px 2px ${ACCENT}33`,
  },
  '&:disabled': {
    background: 'linear-gradient(45deg, #b0bec5 30%, #90a4ae 90%)', // darker gray-blue
    color: '#e0e0e0',
    boxShadow: 'none',
    opacity: 1,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      boxShadow: `0 0 0 2px ${ACCENT}22`,
      borderColor: ACCENT,
    },
    '&.Mui-error': {
      boxShadow: `0 0 0 2px ${ACCENT}22`,
      borderColor: ACCENT,
    },
    '&.Mui-focused.Mui-error': {
      boxShadow: `0 0 0 2px ${ACCENT}22`,
      borderColor: ACCENT,
    },
  },
  '& label.Mui-focused': {
    color: ACCENT,
  },
  '& label.Mui-error': {
    color: ACCENT,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e0e0e0',
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: ACCENT,
  },
  '& .Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: ACCENT,
  },
  '& .Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: ACCENT,
  },
}));

const steps = ['Enter Email', 'Verify OTP', 'Confirm Deletion'];

function validateEmail(email) {
  // Simple email regex
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const DeleteAccount = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleSendOTP = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.post('/v1/user/auth/send-otp-delete-account', { email });
      if (response.data.status) {
        setSuccess('OTP sent successfully!');
        setActiveStep(1);
      }
    } catch (err) {
      console.log(err,"mcksmkmksmksmck");
      setError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndDelete = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.post('/v1/user/auth/verify-otp-delete-account', { email, otp });
      if (response.data.status) {
        setSuccess('Account deleted successfully!');
        setActiveStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (!validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: ACCENT, fontWeight: 'bold' }}>
              Enter your email address to begin account deletion
            </Typography>
            <StyledTextField
              fullWidth
              label="Email"
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              error={!!emailError}
              helperText={emailError}
              sx={{ mb: 3 ,
                 '& .Mui-focused':{
                borderColor:'#e0e0e0 !important'
              }
            }}
            />
            <GradientButton
              fullWidth
              onClick={handleSendOTP}
              disabled={loading || !email || !!emailError}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
            </GradientButton>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: ACCENT, fontWeight: 'bold' }}>
              Enter the OTP sent to your email
            </Typography>
            <StyledTextField
              fullWidth
              label="OTP"
              variant="outlined"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              sx={{ mb: 3 }}
            />
            <GradientButton
              fullWidth
              onClick={handleVerifyAndDelete}
              disabled={loading || !otp}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Delete Account'}
            </GradientButton>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom sx={{ color: ACCENT, fontWeight: 'bold' }}>
              Account Deleted Successfully
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your account has been permanently deleted. Thank you for using our service.
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <AnimatedBubblesBackground />
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        <GradientPaper elevation={3}>
          <BibleLogo />
          <Typography variant="h4" align="center" gutterBottom sx={{ 
            mb: 4, 
            color: ACCENT,
            fontWeight: 'bold',
            textShadow: '1px 1px 2px rgba(0,0,0,0.08)'
          }}>
            Delete Account
          </Typography>

          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{ mb: 4,
              '& .MuiStepIcon-root.Mui-active': { color: ACCENT },
              '& .MuiStepIcon-root.Mui-completed': { color: ACCENT },
              '& .MuiStepLabel-label.Mui-active': { color: ACCENT },
              '& .MuiStepLabel-label.Mui-completed': { color: ACCENT },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: '10px' }}>
              {success}
            </Alert>
          )}

          {renderStepContent(activeStep)}
        </GradientPaper>
      </Container>
    </PageContainer>
  );
};

export default DeleteAccount; 